/**
 * The Go box's best guess for text that matched no destination: debounced,
 * cached per query for the page's life, and cancelled when the text moves on.
 * A failed request counts as "no guess" so the box never waits on it again.
 */
import { useEffect, useState } from 'react';

import { fetchBestGuess } from './bestGuessApi';

/** Typing pauses this long before the server is asked. */
const DEBOUNCE_MS = 400;

/** Text shorter than this is too little to guess from. */
export const MIN_GUESS_LENGTH = 3;

/** Answers already had this page load, by normalised query. */
const answers = new Map<string, string | null>();

/** Test seam: forget every cached answer. */
export function clearBestGuesses(): void {
  answers.clear();
}

/** What the Go box shows for the guess. */
export interface BestGuess {
  /** The guessed destination id, or null for none (yet). */
  guessId: string | null;
  /** True while the guess is debouncing or in flight. */
  guessing: boolean;
}

/**
 * The best guess for `query`, asked for only while `enabled`.
 *
 * @param enabled - The caller's "no destination matched" condition.
 */
export function useBestGuess(query: string, enabled: boolean): BestGuess {
  const text = query.trim();
  const key = text.toLowerCase();
  const asking = enabled && text.length >= MIN_GUESS_LENGTH;
  // A re-render per answer; the answer itself lives in the cache.
  const [, setAnswered] = useState(0);

  useEffect(() => {
    if (!asking || answers.has(key)) return;
    const controller = new AbortController();
    const settle = (id: string | null) => {
      answers.set(key, id);
      setAnswered(n => n + 1);
    };
    const timer = setTimeout(() => {
      fetchBestGuess(text, controller.signal).then(settle, () => {
        if (!controller.signal.aborted) settle(null);
      });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [asking, key, text]);

  if (!asking) return { guessId: null, guessing: false };
  if (answers.has(key)) return { guessId: answers.get(key) ?? null, guessing: false };
  return { guessId: null, guessing: true };
}
